import React from "react";
import { Edit, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header, Field, IconBtn, Modal } from "@/pages/admin/AdminBlog";

const emptyForm = { question: "", answer: "", category: "General", order: 0, active: true };

export default function AdminFaqs() {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [editingId, setEditingId] = React.useState(null);
  const [show, setShow] = React.useState(false);

  const load = async () => setItems((await api.get("/admin/faqs")).data);
  React.useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) };
      if (editingId) await api.put(`/admin/faqs/${editingId}`, payload);
      else await api.post("/admin/faqs", payload);
      toast.success("Saved"); setShow(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/faqs/${id}`); load(); };

  return (
    <div data-testid="admin-faqs">
      <Header title="FAQs" onAdd={() => { setForm(emptyForm); setEditingId(null); setShow(true); }} addLabel="New FAQ" />
      <div className="mt-8 space-y-3">
        {items.map((f) => (
          <div key={f.id} className="card-luxury p-5 flex items-start justify-between gap-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-sky-400">{f.category} · #{f.order}</div>
              <div className="text-white font-medium mt-1">{f.question}</div>
              <div className="text-slate-400 text-sm mt-1 max-w-3xl">{f.answer}</div>
            </div>
            <div className="flex">
              <IconBtn onClick={() => { setForm({ ...f }); setEditingId(f.id); setShow(true); }}><Edit className="w-3.5 h-3.5" /></IconBtn>
              <IconBtn onClick={() => remove(f.id)} variant="danger"><Trash2 className="w-3.5 h-3.5" /></IconBtn>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-slate-500 text-sm text-center py-10">No FAQs yet.</div>}
      </div>

      {show && (
        <Modal title={editingId ? "Edit FAQ" : "New FAQ"} onClose={() => setShow(false)}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Question"><Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} required /></Field>
            <Field label="Answer"><Textarea rows={4} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} required /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
              <Field label="Order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></Field>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-slate-300">Active</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShow(false)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
